import { isNull, isUndefined, min } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GaugeChart from 'react-gauge-chart';

import { useAppDispatch } from '../../../../../hooks/react-redux';
import { IconButton } from '../../../../shared/button/IconButton';
import { Sort, WarningSign } from '../../../../shared/icons';
import { CustomTooltip, InformationTooltip } from '../../../../shared/tooltip';
import styles from '../../Analytics.module.scss';
import { AllDocumentsRiskTolerance, DocumentAnalyticsChart, fetchDocumentRiskToleranceSuccessful, setRiskToleranceDatasetId } from '../../store';
import { DocumentRiskToleranceLevel } from './DocumentRiskToleranceLevel';
import { Spinner } from '../../../../shared/spinner/Spinner';
import { Icon } from '../../../../shared/icon/Icon';

const { red, amber, gold, yellowGreen, green, grey } = styles;

interface MinifiedRiskToleranceChartProps {
    tile: DocumentAnalyticsChart;
    tileInView: null | DocumentAnalyticsChart;
    setTileInView: (tile: DocumentAnalyticsChart | null) => void;
    dimensions: {
        height: number;
        width: number;
    };
    allDocumentsRiskTolerance: AllDocumentsRiskTolerance;
    showOverrideRiskRating: boolean;
    entityId?: number;
    isPreExecution?: boolean;
    marginBottom?: string;
    predefinedChartLabel?: string;
    hideFullScreen?: boolean;
}

export const MinifiedRiskToleranceChart: React.FC<MinifiedRiskToleranceChartProps> = ({
    tile,
    setTileInView,
    tileInView,
    dimensions,
    allDocumentsRiskTolerance,
    showOverrideRiskRating,
    entityId,
    isPreExecution = false,
    marginBottom = '0px',
    predefinedChartLabel,
    hideFullScreen = false
}) => {

    const dispatch = useAppDispatch();
    const [activePercentage, setActivePercentage] = useState<number>(0);

    const toggleTileView = useCallback(() => {
        setTileInView(isNull(tileInView) ? tile : null);
        if (!isNull(tileInView)) {
            dispatch(fetchDocumentRiskToleranceSuccessful(null));
        }
    }, [setTileInView, tileInView, dispatch, tile]);

    const documentRiskPercentage = useMemo(() => {
        if (allDocumentsRiskTolerance.totalDocuments === 0) {
            return null;
        }
        return showOverrideRiskRating ? allDocumentsRiskTolerance.overallOverridePercentage : allDocumentsRiskTolerance.overallPercentage;
    }, [allDocumentsRiskTolerance, showOverrideRiskRating]);

    const { percentage, overridePercentage } = useMemo(() => {
        if (allDocumentsRiskTolerance.totalDocuments === 0) {
            return { percentage: null, overridePercentage: null };
        }
        return { overridePercentage: allDocumentsRiskTolerance.overallOverridePercentage, percentage: allDocumentsRiskTolerance.overallPercentage };
    }, [allDocumentsRiskTolerance]);

    const totalDocuments = useMemo(() => allDocumentsRiskTolerance.totalDocuments, [allDocumentsRiskTolerance]);

    const totalDocumentsLabel = useMemo(() => `Total Documents: ${totalDocuments}`, [totalDocuments]);

    useEffect(() => () => {
        dispatch(fetchDocumentRiskToleranceSuccessful(null));
        dispatch(setRiskToleranceDatasetId(null));
    }, [dispatch]);

    useEffect(() => {
        if (!isNull(documentRiskPercentage) && documentRiskPercentage !== activePercentage) {
            setActivePercentage(documentRiskPercentage);
        }
    }, [activePercentage, documentRiskPercentage]);

    const testId = useMemo(() => {
        let id = 'document-risk-tolerance-chart';
        if (isPreExecution) {
            id = 'pre-execution-'.concat(id);
        }
        if (!isUndefined(entityId)) {
            id = 'entity-'.concat(id);
        }
        return id;
    }, [isPreExecution, entityId]);

    const chartLabel = useMemo(() => {
        if (predefinedChartLabel) {
            return predefinedChartLabel;
        }
        if (isPreExecution) {
            return 'Pre Execution';
        }
        if (!isUndefined(entityId)) {
            return 'Entity';
        }
        return 'Full';
    }, [isPreExecution, entityId, predefinedChartLabel]);

    const tooltipInfo = useMemo(() => {
        let portfolioDescription = 'entire document portfolio';
        if (!isUndefined(entityId)) {
            portfolioDescription = 'documents for the selected entity';
        }
        if (isPreExecution) {
            portfolioDescription = 'pre execution documents';
        }
        return `This is a summary of the risk associated with your ${portfolioDescription}.`;
    }, [entityId, isPreExecution]);

    const wrapperStyle = useMemo(() => ({ height: `${dimensions.height - parseInt(marginBottom)}px`, width: `${dimensions.width}px`, marginBottom, borderBottom: parseInt(marginBottom) > 0 ? `1px dotted ${grey}` : 'none' }), [dimensions, marginBottom]);

    const gaugeWidthPercentage = useMemo(() => min([(dimensions.height / dimensions.width) * 170, 95]), [dimensions]);

    const incompleteIcon = useMemo(() => {
        const tooltipLabel = allDocumentsRiskTolerance.isRecalculating ? 'Recalculating Risk Tolerance' : 'Risk Tolerance Outdated';
        const icon = allDocumentsRiskTolerance.isRecalculating ? <Spinner size={25} color={amber} /> : <Icon icon={WarningSign} fontSize={25} />;
        return (
            <CustomTooltip overlayText={tooltipLabel}>
                <div className={styles.incompleteIcon}>{icon}</div>
            </CustomTooltip>
        );
    }, [allDocumentsRiskTolerance]);
    return (
        <div className={styles.minifiedRiskToleranceChartWrapper} style={wrapperStyle} data-testid={`${testId}-wrapper`}>
            <div className={styles.riskChart}>
                <div className={styles.gaugeChartWrapper}>
                    <GaugeChart
                        nrOfLevels={5}
                        colors={[green, yellowGreen, gold, amber, red]}
                        needleColor={grey}
                        needleBaseColor={grey}
                        hideText
                        percent={(documentRiskPercentage || 0) / 100}
                        animate={activePercentage !== documentRiskPercentage}
                        style={{ width: `${gaugeWidthPercentage}%`, height: '100%', margin: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                        arcPadding={0.02}
                        animDelay={0}
                    />
                </div>
                <DocumentRiskToleranceLevel percentage={percentage} overridePercentage={overridePercentage} showOverrideRiskRating={showOverrideRiskRating} chartIsFullScreen={false} />
                <div className={styles.totalDocumentsWrapper}>{totalDocumentsLabel}</div>
            </div>
            <div className={styles.chartHeader}>
                <div className={styles.chartTitle}>{chartLabel}</div>
                <div className={styles.chartDescription}><InformationTooltip content={tooltipInfo} /></div>
                {!hideFullScreen && <div className={styles.expandIcon}><IconButton icon={Sort} onClick={toggleTileView} fontSize={20} /></div>}
            </div>
            {(allDocumentsRiskTolerance.isRecalculating || allDocumentsRiskTolerance.riskHasUpdated) &&
                <div className={styles.incompleteRiskToleranceWrapper}>{incompleteIcon}</div>
            }
        </div>
    );
};
